import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { LLink } from '../../lib/lang';
import { Card, Btn, ImagePlaceholder, StatusBadge } from './ui';
import { plural } from '../../lib/pay';

// Статус доступа. Вычисляет сервер (product.access), клиент его только показывает.
export const accessLabel = (p, t) => {
  const a = p.access || {};
  if (a.state === 'granted') {
    if (a.unlimited) return { text: t('pl.access.admin'), tone: 'green' };
    if (a.reason === 'tokens') {
      const unit = plural(a.remaining, t('pl.units.segmentation', { returnObjects: true }));
      return { text: t('pl.access.remaining', { count: a.remaining, unit }), tone: 'green' };
    }
    return { text: t('pl.access.open'), tone: 'green' };
  }
  if (a.state === 'restricted') return { text: t('pl.access.adminOnly'), tone: 'gray' };
  if (a.state === 'locked') return { text: t('pl.access.needPayment'), tone: 'yellow' };
  if (a.state === 'contact') return { text: p.price_text || t('pl.access.byAgreement'), tone: 'blue' };
  return { text: t('pl.access.loginToOpen'), tone: 'gray' };
};

export const ProductAction = ({ product, compact = false }) => {
  const { t } = useTranslation();
  const a = product.access || {};
  const detail = `/products/${product.slug}`;
  if (a.state === 'granted' && product.launch_type === 'browser' && product.launch_url) {
    return <Btn to={product.launch_url}>{t('pl.access.launch')}</Btn>;
  }
  if (a.state === 'locked') return <Btn to={`${detail}#tariffs`}>{t('pl.access.buyAccess')}</Btn>;
  if (a.state === 'contact' || product.access_policy === 'contact') {
    return <Btn to={`${detail}#request`} variant={compact ? 'secondary' : 'primary'}>{product.cta_label || t('pl.common.details')}</Btn>;
  }
  if (a.state === 'restricted') return null;      // закрытое тестирование: кнопки запуска нет
  if (a.state === 'login_required') return <Btn to="/login">{t('pl.access.login')}</Btn>;
  return <Btn to={detail} variant="secondary">{t('pl.common.details')}</Btn>;
};

const ProductCard = ({ product }) => {
  const { t } = useTranslation();
  const a = product.access || {};
  const locked = a.state === 'locked' || a.state === 'contact' || a.state === 'login_required' || a.state === 'restricted';
  const label = accessLabel(product, t);
  return (
    <Card className="flex flex-col">
      <LLink to={`/products/${product.slug}`} className="block relative mb-4 overflow-hidden" aria-label={product.name}>
        {product.cover_url ? (
          <img src={product.cover_url} alt={product.name} loading="lazy" className={`w-full h-44 object-cover ${locked && a.state !== 'login_required' ? 'blur-[3px] scale-105' : ''}`} />
        ) : (
          <ImagePlaceholder className={`h-44 ${locked ? 'blur-[2px]' : ''}`} />
        )}
        {locked && a.state !== 'login_required' && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/25">
            <Lock className="text-white drop-shadow" size={30} />
          </span>
        )}
      </LLink>
      <div className="mb-2"><StatusBadge tone={label.tone}>{label.text}</StatusBadge></div>
      <LLink to={`/products/${product.slug}`} className="hover:text-[#C41E3A]">
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{product.name}</h3>
      </LLink>
      <p className="text-gray-700 mb-3 flex-1">{product.short_description}</p>
      {product.features?.length > 0 && (
        <ul className="text-sm text-gray-700 list-disc pl-5 mb-4 space-y-0.5">
          {product.features.slice(0, 3).map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
      <div className="flex flex-wrap gap-3 mt-auto">
        <ProductAction product={product} compact />
        <Btn to={`/products/${product.slug}`} variant="secondary">{t('pl.common.details')}</Btn>
      </div>
    </Card>
  );
};

export default ProductCard;
